import { apiClient } from '@/shared/lib/api';
import type {
  AssistantCardOutcome,
  AssistantBlock,
  AssistantDeleteHistoryTexts,
  AssistantHistoryPage,
  AssistantSessionSummary,
  AssistantMessage,
  AssistantMessagesPage,
  AssistantMoment,
  AssistantMomentOutcome,
  AssistantThread,
  AssistantTranscription,
  AssistantTurnContext,
} from '@/types/assistant';
import { readSse } from '../lib/sse';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('Assistant');

/** What one streamed turn reports while it runs (ADR-001 §6). */
export interface AssistantTurnHandlers {
  onStatus: (state: string) => void;
  onBlock: (block: AssistantBlock | { type: string }) => void;
}

export const assistantService = {
  /** Threads of a building (most recent first), or the general threads. */
  async listThreads(buildingId: string | null): Promise<AssistantThread[]> {
    const query = buildingId
      ? `?filter[building_id]=${encodeURIComponent(buildingId)}`
      : '';
    const res = await apiClient.get<{ data: AssistantThread[] }>(
      `/v1/assistant/threads${query}`
    );
    return res.data;
  },

  async createThread(buildingId: string | null): Promise<AssistantThread> {
    const res = await apiClient.post<{ data: AssistantThread }>(
      '/v1/assistant/threads',
      {
        data: {
          type: 'assistant_thread',
          attributes: buildingId ? { building_id: buildingId } : {},
        },
      }
    );
    return res.data;
  },

  /** The open conversation of the thread. */
  async listMessages(threadId: string): Promise<AssistantMessagesPage> {
    const res = await apiClient.get<{ data: AssistantMessage[] }>(
      `/v1/assistant/threads/${threadId}/messages`
    );
    return { messages: res.data };
  },

  /** "Chat history": every conversation of the thread, newest used first. */
  async listHistory(threadId: string): Promise<AssistantHistoryPage> {
    const res = await apiClient.get<{
      data: AssistantSessionSummary[];
      meta: { delete_history: AssistantDeleteHistoryTexts };
    }>(`/v1/assistant/threads/${threadId}/sessions`);
    return { sessions: res.data, deleteHistory: res.meta.delete_history };
  },

  /** "New conversation": the open one moves to the history. */
  async startSession(threadId: string): Promise<AssistantThread> {
    const res = await apiClient.post<{ data: AssistantThread }>(
      `/v1/assistant/threads/${threadId}/sessions`
    );
    return res.data;
  },

  /** Reopens an earlier conversation as the open one. */
  async resumeSession(threadId: string, session: number): Promise<AssistantThread> {
    const res = await apiClient.post<{ data: AssistantThread }>(
      `/v1/assistant/threads/${threadId}/sessions/${session}/resume`
    );
    return res.data;
  },

  /** "Delete history": every turn and the remembered preferences go. */
  async deleteHistory(threadId: string): Promise<void> {
    await apiClient.delete<void>(`/v1/assistant/threads/${threadId}/history`);
  },

  /**
   * Send a message and read the reply stream. Resolves with the stored
   * assistant message id from `done` (null when the turn was refused or
   * failed; the blocks already said why). Unknown events are logged and
   * ignored, so a newer server never breaks this build.
   */
  async sendMessage(
    threadId: string,
    text: string,
    context: AssistantTurnContext,
    handlers: AssistantTurnHandlers,
    signal?: AbortSignal
  ): Promise<string | null> {
    const response = await apiClient.fetch<Response>(
      `/v1/assistant/threads/${threadId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({
          data: { type: 'assistant_message', attributes: { text, context } },
        }),
        rawResponse: true,
        // The deadline guards the wait for the stream's head. A proxy or
        // worker that buffers the stream delivers the head only with the whole
        // turn (up to four model rounds), so the default 20 s would abort a
        // healthy reply.
        timeoutMs: 90_000,
        signal,
      }
    );
    if (!response.body) return null;

    let messageId: string | null = null;
    await readSse(response.body, ({ event, data }) => {
      let payload: Record<string, unknown>;
      try {
        payload = JSON.parse(data) as Record<string, unknown>;
      } catch {
        logger.warn('Unreadable assistant event', { event });
        return;
      }
      switch (event) {
        case 'status':
          handlers.onStatus(String(payload.state ?? ''));
          break;
        case 'block':
          if (typeof payload.type === 'string') {
            // Shape per `type` is the API's contract; an unknown type is
            // kept and skipped when drawn.
            handlers.onBlock(payload as unknown as AssistantBlock);
          }
          break;
        case 'done':
          messageId = (payload.message_id as string | null) ?? null;
          break;
        default:
          logger.info('Ignoring unknown assistant event', { event });
      }
    });
    return messageId;
  },

  /** Transcribe a recording for the input field; the audio is not stored. */
  async transcribe(
    threadId: string,
    audio: Blob,
    languages: string[]
  ): Promise<string> {
    const form = new FormData();
    const extension = audio.type.includes('mp4') ? 'm4a' : 'webm';
    form.append('audio', audio, `voice.${extension}`);
    languages.forEach((language) => form.append('languages[]', language));
    const res = await apiClient.postForm<{ data: AssistantTranscription }>(
      `/v1/assistant/threads/${threadId}/voice`,
      form
    );
    return res.data.attributes.text;
  },

  /** This client draws both presentations, so it asks for badges too. */
  async listMoments(buildingId: string): Promise<AssistantMoment[]> {
    const res = await apiClient.get<{ data: AssistantMoment[] }>(
      `/v1/assistant/moments?filter[building_id]=${encodeURIComponent(buildingId)}&filter[presentation]=bubble,badge`
    );
    return res.data;
  },

  /** What the user did with an action card of a stored message. */
  async recordCardOutcome(
    threadId: string,
    messageId: string,
    blockIndex: number,
    outcome: AssistantCardOutcome
  ): Promise<void> {
    await apiClient.post<void>(
      `/v1/assistant/threads/${threadId}/messages/${messageId}/card-outcome`,
      {
        data: {
          type: 'assistant_card_outcome',
          attributes: { block_index: blockIndex, outcome },
        },
      }
    );
  },

  async recordMoment(
    moment: AssistantMoment,
    outcome: AssistantMomentOutcome,
    threadId?: string
  ): Promise<void> {
    const { building_id, render_id } = moment.attributes;
    await apiClient.post<void>(`/v1/assistant/moments/${moment.id}`, {
      data: {
        type: 'assistant_moment',
        attributes: {
          building_id,
          outcome,
          ...(render_id ? { render_id } : {}),
          ...(threadId ? { thread_id: threadId } : {}),
        },
      },
    });
  },
};
